<template>
  <ul v-if="isComponentReady" :class="[$style.list, 'hide-on-print']">
    <UbsImmoCheck
      v-if="isUbsImmoCheckVisible"
      :listing="listing"
      @trackPartnerLink="trackPartnerLinkFunction"
    >
    </UbsImmoCheck>

    <UbsPreQualifyWidget
      v-if="isUbsPreQualifyVisible"
      :listing="listing"
      @trackPartnerLink="trackPartnerLinkFunction"
    >
    </UbsPreQualifyWidget>

    <MortgageAffordability v-if="isMortgageAffordabilityVisible"> </MortgageAffordability>

    <SwissCautionRentalGuarantee
      v-if="isSwissCautionRentalGuaranteeVisible"
      :listing="listing"
      @trackPartnerLink="trackPartnerLinkFunction"
    ></SwissCautionRentalGuarantee>

    <Crif v-if="isCrifVisible" @trackPartnerLink="trackPartnerLinkFunction"> </Crif>
  </ul>
</template>

<script lang="ts" setup>
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { computed, onMounted, ref } from 'vue';

import {
  isListingValidForCrif,
  isListingValidForMortgageCalculations,
  isListingValidForSwissCaution,
  isListingValidForUbsImmoCheck,
  isListingValidForUbsPreQualify,
} from '@/components/PartnerIntegrations';
import Crif from '@/components/PartnerIntegrations/Crif/Crif.vue';
import MortgageAffordability from '@/components/PartnerIntegrations/MortgageAffordability/MortgageAffordability.vue';
import SwissCautionRentalGuarantee from '@/components/PartnerIntegrations/SwissCautionRentalGuarantee/SwissCautionRentalGuarantee.vue';
import UbsImmoCheck from '@/components/PartnerIntegrations/UbsImmoCheck/UbsImmoCheck.vue';
import UbsPreQualifyWidget from '@/components/PartnerIntegrations/UbsPreQualify/UbsPreQualifyWidget.vue';
import { InteractionGoal } from '@/helpers/GTM/GTMParameters';

import { useI18n } from 'vue-i18n';
import { trackPartnerLink } from '../functions/tracking';

const { locale } = useI18n();

defineOptions({
  name: 'PartnerListFinance',
});

interface Props {
  listing: Listing;
}
const props = defineProps<Props>();

const isComponentReady = ref(false);

const isUbsImmoCheckVisible = computed((): boolean => {
  return isListingValidForUbsImmoCheck(props.listing, locale.value);
});
const isUbsPreQualifyVisible = computed((): boolean => {
  return isListingValidForUbsPreQualify(props.listing);
});
const isMortgageAffordabilityVisible = computed((): boolean => {
  return isListingValidForMortgageCalculations(props.listing);
});
const isSwissCautionRentalGuaranteeVisible = computed((): boolean => {
  return isListingValidForSwissCaution(props.listing);
});
const isCrifVisible = computed((): boolean => {
  return isListingValidForCrif(props.listing);
});

onMounted(() => {
  // Render the component only on the client side
  isComponentReady.value = true;
});

const trackPartnerLinkFunction = (externalUrl: string, interactionGoal: InteractionGoal): void => {
  trackPartnerLink(externalUrl, interactionGoal, props.listing.id);
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

.list {
  display: flex;
  flex-direction: column;
  gap: $space-75;
  justify-content: space-between;
  padding: 0;
  margin: $space-150 0;
  list-style: none;
}
</style>
